import React, { useRef } from 'react';
import PhoneInput from 'react-phone-input-2';

import { TableContainer } from 'apps/auth/components/common/components/TableRow/styles';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import {
    CATEGORY_TRANSFER_TO_CASH,
    CATEGORY_TRANSFER_TO_CASH_UNITELLER,
    CATEGORY_TRANSFER_TO_HOME_DELIVERY,
    CATEGORY_TRANSFER_TO_INTERAC,
    CATEGORY_TRANSFER_TO_LIBRE,
    CATEGORY_TRANSFER_TO_PEOPLE_TRUST_INTERAC,
    STATUS_OK,
} from 'apps/auth/constants/Constants';
import { MEXICO, PHILIPPINES, VIETNAM } from 'apps/auth/constants/WellKnownCountries';
import { getToAccount, getTableTextColor } from 'apps/auth/helpers/historyUtils';
import { usePageMessages } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { useCountryMobileMask } from 'apps/auth/hooks/useCountryMobileMask';
import pallets from 'apps/auth/theme/pallets';

import CheckTableRow from './CheckTableRow';
import PickupPopup from './PickupPopup';

const RecipientDetails = (props) => {
    const popupRef = useRef(null);
    const { theme } = useThemeDetector();
    const cheque = props.cheque;
    const {
        chequeDeliveryHeader,
        chequeBlockAddress,
        chequeMobilePhoneHeader,
        chequeRecipientHeader,
        chequeBlockBankForPickupHeader,
        chequeBlockToLibre,
        transferPaysend,
        chequeBlockPickupHeader,
        chequeBlockProviderHeader,
        chequeBlockBankHeader,
        chequeRecipientInterac,
        chequeRecipientPeopleTrustInterac,
        ...messages
    } = usePageMessages();

    const checkStatusFailedColor = theme === 'dark' ? pallets.dark.grey_40_lc : pallets.light.grey_40_lc;
    const checkStatusSuccessColor = theme === 'dark' ? pallets.dark.black : pallets.light.black;

    const hasPhoneInput =
        (cheque.providerToCategory === CATEGORY_TRANSFER_TO_CASH ||
            cheque.providerToCategory === CATEGORY_TRANSFER_TO_CASH_UNITELLER ||
            cheque.providerToCategory === CATEGORY_TRANSFER_TO_HOME_DELIVERY) &&
        cheque.toPhoneNumber &&
        cheque.toPhoneNumber !== 'null';

    const { defaultMask, masks, isReady, isMaskReadyForIso2 } = useCountryMobileMask(cheque.toBankCountryCode);

    const getToBankName = (chequeToBankName) => {
        const isPickup =
            chequeToBankName.providerToCategory === CATEGORY_TRANSFER_TO_CASH || chequeToBankName.providerToCategory === CATEGORY_TRANSFER_TO_CASH_UNITELLER;
        const isPhilippinesOrMexicoPickup =
            chequeToBankName.toCountryId === PHILIPPINES ||
            chequeToBankName.toCountryId === MEXICO ||
            chequeToBankName.providerToCategory === CATEGORY_TRANSFER_TO_CASH_UNITELLER;
        const headerPickUp = chequeToBankName.toCountryId === VIETNAM ? chequeBlockBankForPickupHeader : chequeBlockPickupHeader;
        const header = isPickup ? headerPickUp : chequeBlockBankHeader;
        const title = isPickup
            ? chequeToBankName.toBankName
            : `${chequeToBankName.toBankName}${chequeToBankName.toBankCountryName ? ` · ${chequeToBankName.toBankCountryName}` : ''}`;
        return (
            <CheckTableRow title={header} cheque={cheque}>
                <Row flex align="center">
                    {title}
                    {isPickup && isPhilippinesOrMexicoPickup && (
                        <DiskButton
                            size="small"
                            iconName="info-rounded"
                            iconColor={getTableTextColor(chequeToBankName)}
                            type="button"
                            raised={false}
                            onClick={() => popupRef.current?.open(<PickupPopup networkName={title} popupRef={popupRef} />)}
                        />
                    )}
                </Row>
            </CheckTableRow>
        );
    };

    const getDeliveryOption = () => {
        if (cheque.providerToCategory === CATEGORY_TRANSFER_TO_LIBRE) {
            return (
                <>
                    <CheckTableRow title={chequeMobilePhoneHeader} cheque={cheque}>
                        {props.cheque.toPhoneNumber}
                    </CheckTableRow>
                    <CheckTableRow title={chequeBlockToLibre} cheque={cheque}>
                        {transferPaysend}
                    </CheckTableRow>
                </>
            );
        }

        return (
            <CheckTableRow title={chequeDeliveryHeader} cheque={cheque}>
                {getToAccount(
                    cheque.providerToCategory,
                    cheque.toCountryId,
                    cheque.providerToCategory === CATEGORY_TRANSFER_TO_CASH_UNITELLER ? null : cheque.accountTo,
                    messages,
                )}
            </CheckTableRow>
        );
    };

    return (
        <>
            <Separator />
            <TableContainer>
                {cheque.nameTo && cheque.nameTo.trim().length > 0 && (
                    <CheckTableRow title={chequeRecipientHeader} cheque={cheque}>
                        {cheque.nameTo}
                    </CheckTableRow>
                )}
                {hasPhoneInput && (
                    <CheckTableRow title={chequeMobilePhoneHeader} cheque={cheque}>
                        {isReady && isMaskReadyForIso2 && (
                            <PhoneInput
                                specialLabel={''}
                                value={cheque.toPhoneNumber}
                                defaultMask={defaultMask}
                                masks={masks}
                                disableDropdown={true}
                                inputProps={{ readOnly: true }}
                                inputStyle={{
                                    fontSize: 'inherit',
                                    lineHeight: 'inherit',
                                    fontFamily: 'inherit',
                                    border: 'none',
                                    background: 'inherit',
                                    color: cheque.status > STATUS_OK ? checkStatusFailedColor : checkStatusSuccessColor,
                                }}
                            />
                        )}
                    </CheckTableRow>
                )}
                {getDeliveryOption()}
                {cheque.providerToCategory === CATEGORY_TRANSFER_TO_INTERAC && (
                    <CheckTableRow title={chequeBlockProviderHeader} cheque={cheque}>
                        {chequeRecipientInterac}
                        {' · '}
                        {cheque.toBankCountryName}
                    </CheckTableRow>
                )}
                {cheque.providerToCategory === CATEGORY_TRANSFER_TO_PEOPLE_TRUST_INTERAC && (
                    <CheckTableRow title={chequeBlockProviderHeader} cheque={cheque}>
                        {chequeRecipientPeopleTrustInterac}
                        {' · '}
                        {cheque.toBankCountryName}
                    </CheckTableRow>
                )}
                {cheque.toBankName && getToBankName(cheque)}
                {cheque.toAddress && cheque.toHomeDelivery && (
                    <CheckTableRow title={chequeBlockAddress} cheque={cheque}>
                        <DangerouslyHtml type="span" dangerouslyHtml={cheque.toAddress} />
                    </CheckTableRow>
                )}
            </TableContainer>
            <PopupNew ref={popupRef} />
        </>
    );
};
export default RecipientDetails;
